import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Loader2, Upload, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForTalentPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [formData, setFormData] = useState({
    full_name: "", email: "", phone: "", linkedin_url: "",
    current_role: "", current_company: "", years_experience: "",
    desired_roles: [], salary_expectation: "", availability: "", about: ""
  });

  const roleOptions = [
    // IC Roles
    "Software Engineer", "Senior Engineer", "Product Manager", "Growth Marketer",
    "Account Executive", "SDR/BDR", "Customer Success Rep", "Data Analyst",
    // Manager Roles
    "Engineering Manager", "Tech Lead", "Product Lead", "Sales Manager",
    "Marketing Manager", "CS Manager",
    // Leadership Roles
    "VP Engineering", "CTO", "VP Product", "CPO",
    "VP Sales", "CRO", "VP Marketing", "CMO",
    "VP Customer Success", "Head of Growth", "COO"
  ];

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      desired_roles: prev.desired_roles.includes(role)
        ? prev.desired_roles.filter(r => r !== role)
        : [...prev.desired_roles, role]
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 10 * 1024 * 1024) {
      setResumeFile(file);
    } else if (file) {
      toast.error("File too large (max 10MB)");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.desired_roles.length === 0) {
      toast.error("Please select at least one desired role");
      return;
    }
    setSubmitting(true);
    try {
      let resume_filename = null;
      if (resumeFile) {
        const fileData = new FormData();
        fileData.append('file', resumeFile);
        const uploadRes = await axios.post(`${API}/upload-resume`, fileData);
        resume_filename = uploadRes.data.filename;
      }

      await axios.post(`${API}/candidates`, {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        resume_filename
      });
      setSubmitted(true);
      toast.success("Profile submitted successfully!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const talentFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "How do I join the Magnus Kor talent network?", "acceptedAnswer": { "@type": "Answer", "text": "Submit your profile through our For Talent page. Include your experience, desired roles, and salary expectations. Our team reviews every submission and reaches out when relevant opportunities match your profile." }},
      { "@type": "Question", "name": "What types of roles does Magnus Kor recruit for?", "acceptedAnswer": { "@type": "Answer", "text": "We recruit across Engineering (Software Engineers to CTOs), Product (PMs to CPOs), Growth & Marketing, Go-to-Market (SDRs to VPs of Sales), and Customer Success at VC-backed SaaS startups." }},
      { "@type": "Question", "name": "Is there a fee for candidates?", "acceptedAnswer": { "@type": "Answer", "text": "No. Magnus Kor's services are completely free for candidates. Our fees are paid by the hiring companies." }},
      { "@type": "Question", "name": "Do you offer remote startup jobs?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Many of the startups we work with offer remote or hybrid roles. We place candidates across the US, UK, Europe, and APAC in both remote-first and hybrid companies." }},
      { "@type": "Question", "name": "How long does the placement process take?", "acceptedAnswer": { "@type": "Answer", "text": "From initial conversation to offer, the typical timeline is 2-6 weeks depending on the role and company. We keep candidates informed throughout the process." }}
    ]
  };

  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO 
        title="For Talent - Find Your Next Startup Role"
        description="Join our network of exceptional talent. We connect engineers, product managers, growth marketers, and leaders with high-growth VC-backed SaaS startups. IC to executive roles, remote and hybrid."
        keywords="startup jobs, SaaS careers, engineering jobs VC startups, product manager jobs startups, growth marketing SaaS, customer success careers, tech jobs London, remote startup jobs, Series A startup jobs, startup executive roles, SaaS startup careers 2026"
        canonical="https://magnuskor.com/for-talent"
        jsonLd={talentFaqSchema}
      />
      {/* Hero */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-overline">For Talent</span>
              <h1 className="heading-hero mt-4 mb-6">
                Your next opportunity awaits
              </h1>
              <p className="text-body-lg mb-8">
                Whether you're an engineer shipping code, a PM defining products, or a leader building teams—we connect exceptional people with startups where they can have real impact.
              </p>
              <a href="#join-network" className="btn-luxury btn-luxury-dark">
                Join Our Network
                <ArrowRight size={16} />
              </a>
            </div>
            <div className="relative">
              <img loading="lazy"
                src="/images/site/unsplash-1758873268631-a7403e.jpg"
                alt="Talent collaborating at a high-growth VC-backed SaaS startup office"
                className="w-full img-desaturate hover:filter-none transition-all duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Us - Honest Value Prop */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="max-w-2xl mb-16">
            <span className="text-overline">Why Join Our Network</span>
            <h2 className="heading-section mt-4 mb-6">What we offer candidates</h2>
            <p className="text-body-lg">
              We work with VC-backed SaaS startups hiring across all levels—from your first role to your tenth. Here's what you can expect from us.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Curated Opportunities Only",
                desc: "We'll only reach out when we have a role that genuinely matches your background and interests. No spam, no irrelevant job blasts."
              },
              {
                title: "Honest Conversations",
                desc: "We'll give you real information about opportunities—the good and the challenging. No overselling roles that aren't right."
              },
              {
                title: "Respect for Your Time",
                desc: "We won't waste your time with roles that don't fit. And we'll never ghost you—if something changes, we'll tell you."
              }
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 border border-gray-100">
                <h3 className="font-display text-xl mb-4">{item.title}</h3>
                <p className="text-body text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Look For - Updated for all levels */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="text-overline">Who We Work With</span>
              <h2 className="heading-section mt-4 mb-8">Talent at every level</h2>
              <p className="text-body mb-8">
                We place people across the full spectrum—from individual contributors to executives. Here's what we look for:
              </p>
              
              <div className="space-y-4">
                {[
                  "Passion for building at startups",
                  "Track record of impact and ownership",
                  "Excited about high-growth environments",
                  "Strong problem-solving and communication skills",
                  "Alignment with startup culture and pace"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check size={18} className="text-[#B8860B]" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-[#FAF9F6] border-l-2 border-[#B8860B]">
                <p className="text-sm text-gray-600">
                  <strong>Experience levels we place:</strong> Early career (2+ years), Mid-level (4-6 years), Senior (7+ years), Leadership (Directors, VPs, C-suite)
                </p>
              </div>
            </div>

            <div className="bg-[#1A1A1A] p-10 md:p-12 text-white">
              <span className="text-[#B8860B] text-xs tracking-[0.2em] uppercase">Roles We Fill</span>
              <h3 className="font-display text-2xl mt-4 mb-6">Functions we specialize in</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  "Engineering",
                  "Product",
                  "Growth & Marketing",
                  "Sales",
                  "Customer Success",
                  "Operations"
                ].map((func, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#B8860B] rounded-full"></span>
                    <span className="text-gray-300">{func}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-500 mt-6 text-sm">
                From your first startup role to your next leadership position—we help you find the right fit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="join-network" className="py-24 bg-[#FAF9F6]">
        <div className="max-w-3xl mx-auto px-8 md:px-16">
          <div className="text-center mb-12">
            <span className="text-overline">Join Our Network</span>
            <h2 className="heading-section mt-4 mb-4">Tell us about yourself</h2>
            <p className="text-body">
              We'll reach out when we have opportunities that match your background.
            </p>
          </div>

          {submitted ? (
            <div className="bg-white p-12 text-center">
              <Check size={48} className="text-[#B8860B] mx-auto mb-4" />
              <h3 className="font-display text-2xl mb-2">Welcome to the network</h3>
              <p className="text-body">We'll be in touch when we find opportunities that match your profile.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10 space-y-8" data-testid="candidate-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Full Name *</label>
                  <input type="text" required value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} className="input-luxury border-b border-gray-200" data-testid="candidate-name" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Email *</label>
                  <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="input-luxury border-b border-gray-200" data-testid="candidate-email" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">LinkedIn URL</label>
                  <input type="url" value={formData.linkedin_url} onChange={e => setFormData({...formData, linkedin_url: e.target.value})} className="input-luxury border-b border-gray-200" placeholder="https://linkedin.com/in/..." />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Phone</label>
                  <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="input-luxury border-b border-gray-200" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Current Role *</label>
                  <input type="text" required value={formData.current_role} onChange={e => setFormData({...formData, current_role: e.target.value})} className="input-luxury border-b border-gray-200" data-testid="candidate-role" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Current Company</label>
                  <input type="text" value={formData.current_company} onChange={e => setFormData({...formData, current_company: e.target.value})} className="input-luxury border-b border-gray-200" />
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Years of Experience</label>
                  <input type="number" value={formData.years_experience} onChange={e => setFormData({...formData, years_experience: e.target.value})} className="input-luxury border-b border-gray-200" />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 block mb-3">Desired Roles * <span className="text-gray-400">(select all that apply)</span></label>
                <div className="flex flex-wrap gap-2">
                  {roleOptions.map(role => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => toggleRole(role)}
                      className={`px-4 py-2 text-sm border transition-colors ${
                        formData.desired_roles.includes(role)
                          ? "bg-[#B8860B] border-[#B8860B] text-white"
                          : "bg-white border-gray-200 text-gray-600 hover:border-gray-400"
                      }`}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Salary Expectation</label>
                  <select value={formData.salary_expectation} onChange={e => setFormData({...formData, salary_expectation: e.target.value})} className="input-luxury border-b border-gray-200 bg-transparent">
                    <option value="">Select range</option>
                    <option value="$150k-$200k">$150k-$200k</option>
                    <option value="$200k-$250k">$200k-$250k</option>
                    <option value="$250k-$300k">$250k-$300k</option>
                    <option value="$300k-$400k">$300k-$400k</option>
                    <option value="$400k+">$400k+</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 block mb-2">Availability</label>
                  <select value={formData.availability} onChange={e => setFormData({...formData, availability: e.target.value})} className="input-luxury border-b border-gray-200 bg-transparent">
                    <option value="">Select availability</option>
                    <option value="Immediately">Immediately</option>
                    <option value="2 weeks">2 weeks notice</option>
                    <option value="1 month">1 month notice</option>
                    <option value="Passively looking">Passively looking</option>
                  </select>
                </div>
              </div>

              {/* Resume Upload */}
              <div>
                <label className="text-sm text-gray-600 block mb-2">Resume (PDF, DOC, DOCX)</label>
                <div className="border border-dashed border-gray-300 p-6 text-center bg-[#FAF9F6]">
                  {resumeFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-sm">{resumeFile.name}</span>
                      <button type="button" onClick={() => setResumeFile(null)} className="text-gray-400 hover:text-red-500">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" />
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <Upload size={24} />
                        <span className="text-sm">Click to upload</span>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 block mb-2">Anything else you'd like us to know?</label>
                <textarea rows={4} value={formData.about} onChange={e => setFormData({...formData, about: e.target.value})} className="input-luxury border border-gray-200 p-4 resize-none w-full" placeholder="Your ideal next role, what you're looking for in a company..." />
              </div>

              <button type="submit" disabled={submitting} className="btn-luxury btn-luxury-dark w-full" data-testid="candidate-submit">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : "Join Network"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ForTalentPage;
